import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { FieldShell } from "@/components/ui/Field";
import { Select } from "@/components/ui/Select";
import { tone, type Tone } from "@/components/ui/tone";
import {
  addMonths,
  calendarFor,
  civilOf,
  dayNumberOf,
  formatDay,
  formatNumber,
  gregorianOf,
  monthGrid,
  monthLabel,
  startOfMonth,
  wallClockIn,
  weekdayNames,
  weekStartFor,
  type WallClock,
} from "@/lib/calendar";
import { cn } from "@/lib/cn";
import { useI18n } from "@/lib/i18n";

export type DateTimeFieldProps = {
  label: string;
  value: WallClock;
  onChange: (value: WallClock) => void;

  timeZone: string;
  hint?: string | undefined;
  error?: string | undefined;
  tone?: Tone | undefined;
  disabled?: boolean | undefined;
};

const PANEL_WIDTH = 312;
const GAP = 6;

type Placement = { left: number; top: number; width: number };

const MINUTE_STEP = 5;

export function DateTimeField({
  label,
  value,
  onChange,
  timeZone,
  hint,
  error,
  tone: which,
  disabled = false,
}: DateTimeFieldProps) {
  const id = useId();
  const { locale, t, dir } = useI18n();
  const palette = tone(which);
  const calendar = calendarFor(locale);
  const weekStart = weekStartFor(locale);

  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const selected = dayNumberOf(value.year, value.month, value.day);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => startOfMonth(selected, calendar));
  const [place, setPlace] = useState<Placement | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);

  const today = (() => {
    const now = wallClockIn(new Date(), timeZone);
    return dayNumberOf(now.year, now.month, now.day);
  })();

  const measure = useCallback(() => {
    const element = trigger.current;
    if (element === null) return;
    const rect = element.getBoundingClientRect();
    const width = Math.min(PANEL_WIDTH, globalThis.innerWidth - GAP * 4);

    const needed = 380;
    const below = globalThis.innerHeight - rect.bottom - GAP * 2;
    const flip = below < needed && rect.top > below;
    const start = dir === "rtl" ? rect.right - width : rect.left;
    setPlace({
      left: Math.max(GAP, Math.min(start, globalThis.innerWidth - width - GAP)),
      top: flip ? Math.max(GAP, rect.top - GAP - needed) : rect.bottom + GAP,
      width,
    });
  }, [dir]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    setHost(trigger.current?.closest("dialog") ?? document.body);
    measure();
    const onScroll = () => {
      measure();
    };
    globalThis.addEventListener("scroll", onScroll, true);
    globalThis.addEventListener("resize", onScroll);
    return () => {
      globalThis.removeEventListener("scroll", onScroll, true);
      globalThis.removeEventListener("resize", onScroll);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target === null) return;
      if (trigger.current?.contains(target) === true) return;
      if (panel.current?.contains(target) === true) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      event.stopPropagation();
      setOpen(false);
      trigger.current?.focus();
    };
    globalThis.addEventListener("pointerdown", onDown, true);
    globalThis.addEventListener("keydown", onKey, true);
    return () => {
      globalThis.removeEventListener("pointerdown", onDown, true);
      globalThis.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const pick = (day: number) => {
    const gregorian = gregorianOf(day);
    onChange({ ...gregorian, hour: value.hour, minute: value.minute });
    setOpen(false);
    trigger.current?.focus();
  };

  const setTime = (hour: number, minute: number) => {
    onChange({ ...value, hour, minute });
  };

  const days = monthGrid(view, weekStart);
  const headings = weekdayNames(locale, weekStart);
  const viewMonth = civilOf(view, calendar).month;

  const hours = Array.from({ length: 24 }, (_unused, hour) => ({
    value: String(hour),
    label: formatNumber(hour, locale, 2),
  }));
  const minutes = Array.from({ length: 60 / MINUTE_STEP }, (_unused, index) => index * MINUTE_STEP)

    .concat(value.minute % MINUTE_STEP === 0 ? [] : [value.minute])
    .toSorted((a, b) => a - b)
    .map((minute) => ({ value: String(minute), label: formatNumber(minute, locale, 2) }));

  return (
    <FieldShell
      describedBy={`${id}-help`}
      error={error}
      hint={hint}
      htmlFor={id}
      label={label}
      tone={which}
    >
      <button
        aria-describedby={`${id}-help`}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-xl border px-3.5 text-start text-sm transition-colors",
          "min-h-11 disabled:cursor-not-allowed disabled:opacity-60",
          palette.control,
          palette.hover,
          open && "border-brand",
          error !== undefined && error !== "" && "border-danger",
        )}
        disabled={disabled}
        id={id}
        onPointerDown={(event) => {
          event.preventDefault();
          if (open) {
            setOpen(false);
            return;
          }
          setView(startOfMonth(selected, calendar));
          setOpen(true);
        }}
        ref={trigger}
        type="button"
      >
        <span className="min-w-0 truncate">
          {formatDay(selected, locale, { weekday: "short", day: "numeric", month: "long" })}
          {" · "}
          {formatNumber(value.hour, locale, 2)}:{formatNumber(value.minute, locale, 2)}
        </span>
        <CalendarDays aria-hidden className={cn("h-4 w-4 shrink-0", palette.muted)} />
      </button>

      {open && place !== null && host !== null
        ? createPortal(
            <div
              aria-label={label}
              className={cn("fixed z-50 rounded-xl border p-3", palette.popup)}
              ref={panel}
              role="dialog"
              style={{ left: place.left, top: place.top, width: place.width }}
            >
              <div className="flex items-center justify-between gap-1">
                <PanelButton
                  label={t("calendar.previousMonth")}
                  onClick={() => {
                    setView(addMonths(view, -1, calendar));
                  }}
                >
                  {dir === "rtl" ? (
                    <ChevronRight aria-hidden className="h-4 w-4" />
                  ) : (
                    <ChevronLeft aria-hidden className="h-4 w-4" />
                  )}
                </PanelButton>
                <span aria-live="polite" className="text-sm font-medium">
                  {monthLabel(view, locale)}
                </span>
                <PanelButton
                  label={t("calendar.nextMonth")}
                  onClick={() => {
                    setView(addMonths(view, 1, calendar));
                  }}
                >
                  {dir === "rtl" ? (
                    <ChevronLeft aria-hidden className="h-4 w-4" />
                  ) : (
                    <ChevronRight aria-hidden className="h-4 w-4" />
                  )}
                </PanelButton>
              </div>

              <div className="mt-2 grid grid-cols-7 gap-0.5">
                {headings.map((heading, index) => (
                  <span
                    aria-hidden
                    className={cn("py-1 text-center text-[0.7rem]", palette.muted)}
                    key={`${heading}-${String(index)}`}
                  >
                    {heading}
                  </span>
                ))}

                {days.map((day) => {
                  const civil = civilOf(day, calendar);
                  const outside = civil.month !== viewMonth;
                  const isSelected = day === selected;
                  return (
                    <button
                      aria-current={day === today ? "date" : undefined}
                      aria-pressed={isSelected}
                      className={cn(
                        "flex h-9 items-center justify-center rounded-lg text-sm transition-colors",
                        outside && !isSelected && "opacity-35",
                        isSelected
                          ? "bg-brand font-semibold text-brand-ink"
                          : cn(palette.hover, day === today && "font-semibold text-brand"),
                      )}
                      key={day}
                      onClick={() => {
                        pick(day);
                      }}
                      type="button"
                    >
                      {formatNumber(civil.day, locale)}
                    </button>
                  );
                })}
              </div>

              <div className={cn("mt-3 flex items-end gap-2 border-t pt-3", palette.line)}>
                <div className="flex flex-1 items-center gap-1.5">
                  <Select
                    onChange={(next) => {
                      setTime(Number(next), value.minute);
                    }}
                    options={hours}
                    tone={which}
                    value={String(value.hour)}
                  />
                  <span aria-hidden className={palette.muted}>
                    :
                  </span>
                  <Select
                    onChange={(next) => {
                      setTime(value.hour, Number(next));
                    }}
                    options={minutes}
                    tone={which}
                    value={String(value.minute)}
                  />
                </div>
                <PanelButton
                  label={t("calendar.today")}
                  onClick={() => {
                    setView(startOfMonth(today, calendar));
                    pick(today);
                  }}
                  wide
                >
                  {t("calendar.today")}
                </PanelButton>
              </div>
            </div>,
            host,
          )
        : null}
    </FieldShell>
  );
}

function PanelButton({
  label,
  onClick,
  wide = false,
  children,
}: {
  label: string;
  onClick: () => void;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      aria-label={wide ? undefined : label}
      className={cn(
        "inline-flex min-h-9 items-center justify-center rounded-lg text-sm",
        "text-ink-muted hover:bg-surface-sunken hover:text-ink",
        wide ? "px-3" : "w-9",
      )}
      onClick={onClick}
      title={label}
      type="button"
    >
      {children}
    </button>
  );
}
